/** Parser de Server-Sent Events sobre um `ReadableStream` lido por `fetch`.
 *
 *  Puro e sem estado global, para ser testado sozinho. O texto chega em pedaços
 *  que não respeitam a fronteira dos eventos, então o parser acumula até achar a
 *  linha em branco que fecha um bloco. Dentro do bloco, `event:` dá o nome,
 *  `data:` pode aparecer em várias linhas e é juntado com quebra de linha, e
 *  linhas que começam com dois-pontos são comentários, que o servidor usa como
 *  batimento a cada quinze segundos enquanto o modelo pensa. */

export interface BlocoSse {
  evento: string
  dados: string
}

/** Interpreta um bloco já isolado. Devolve null quando não há dado nenhum. */
export function parseBlocoSse(bloco: string): BlocoSse | null {
  let evento = 'message'
  const dados: string[] = []
  let temDados = false
  for (const linhaCrua of bloco.split('\n')) {
    const linha = linhaCrua.endsWith('\r') ? linhaCrua.slice(0, -1) : linhaCrua
    if (linha === '' || linha.startsWith(':')) continue
    const separador = linha.indexOf(':')
    const campo = separador === -1 ? linha : linha.slice(0, separador)
    let valor = separador === -1 ? '' : linha.slice(separador + 1)
    if (valor.startsWith(' ')) valor = valor.slice(1)
    if (campo === 'event') evento = valor
    else if (campo === 'data') {
      dados.push(valor)
      temDados = true
    }
  }
  if (!temDados) return null
  return { evento, dados: dados.join('\n') }
}

export interface ParserSse {
  /** Recebe um pedaço do fluxo e devolve os blocos que ficaram completos. */
  alimentar(pedaco: string): BlocoSse[]
  /** Fecha o fluxo: o que sobrou no acumulador vira o último bloco. */
  finalizar(): BlocoSse[]
}

export function criarParserSse(): ParserSse {
  let acumulado = ''

  function extrair(): BlocoSse[] {
    const blocos: BlocoSse[] = []
    // Fronteira de bloco: uma linha em branco, com ou sem retorno de carro.
    const separador = /\r?\n\r?\n/
    let corte = acumulado.search(separador)
    while (corte !== -1) {
      const bruto = acumulado.slice(0, corte)
      const tamanho = acumulado.match(separador)?.[0].length ?? 2
      acumulado = acumulado.slice(corte + tamanho)
      const bloco = parseBlocoSse(bruto)
      if (bloco) blocos.push(bloco)
      corte = acumulado.search(separador)
    }
    return blocos
  }

  return {
    alimentar(pedaco) {
      acumulado += pedaco
      return extrair()
    },
    finalizar() {
      const blocos = extrair()
      if (acumulado.trim()) {
        const ultimo = parseBlocoSse(acumulado)
        if (ultimo) blocos.push(ultimo)
      }
      acumulado = ''
      return blocos
    },
  }
}
